import { useEffect, useRef, useState } from "react";
import { ActionIcon, Anchor, Box, Button, Card, Collapse, CopyButton, Group, Image, NumberInput, Select, SimpleGrid, Stack, Tabs, Text, Textarea, TextInput, Title, Tooltip } from "@mantine/core";
import { IconBarcode, IconCamera, IconMessageCircle, IconPhotoUp, IconRefresh, IconStethoscope, IconThumbDown, IconThumbUp, IconX } from "@tabler/icons-react";
import { imageToDataUri } from "@passio/api-client";
import { giCoachFromOutput, sinkOutput, type GiCoachResponse, type GiLevel, type GiTrigger } from "@passio/easyai-client";
import { easyai } from "../lib/clients";
import { PageHeader } from "../theme/components";
import { RequireFeature } from "../components/common";
import { JsonView } from "../components/JsonView";
import { BarcodeScanner } from "../components/BarcodeScanner";
import { ChainError, RunProgressCard, useChainRun, type RunStage } from "../components/ChainRun";

const CHAIN_KEY = "gi_coach";
const MAX_IMAGES = 5;
const STAGES: RunStage[] = [
  { label: "Reading your meal", throughNode: 1 },
  { label: "Checking guidance", throughNode: 2 },
  { label: "Writing your answer", throughNode: 3 },
];

interface Details {
  question: string;
  food_text: string;
  symptoms: string;
  treatment_stage: string;
  barcodes: string;
  medication_name: string;
  weeks_on_treatment: number | "";
  fluid_restriction: string;
  dietary_constraints: string;
  additional_context: string;
  foods: string;
}
const EMPTY_DETAILS: Details = {
  question: "",
  food_text: "",
  symptoms: "",
  treatment_stage: "unknown",
  barcodes: "",
  medication_name: "",
  weeks_on_treatment: "",
  fluid_restriction: "unknown",
  dietary_constraints: "",
  additional_context: "",
  foods: "",
};

const STARTERS = [
  "I'm 3 weeks in, feeling a bit queasy, and had a burger and fries for lunch. Is that why?",
  "What's a good breakfast when I'm not very hungry?",
  "Why do fatty meals feel heavy on a GLP-1?",
  "I had pizza and a soda last night and felt bloated. What could help next time?",
];

const LEVELS: Record<GiLevel, { icon: string; label: string; plain: string }> = {
  low: { icon: "✓", label: "Low concern", plain: "Likely to sit gently" },
  moderate: { icon: "!", label: "Moderate", plain: "May feel heavy for some" },
  high: { icon: "▲", label: "High concern", plain: "More likely to cause discomfort" },
  unknown: { icon: "?", label: "Unknown", plain: "Not enough to judge" },
};

function LevelBadge({ level, plain = true }: { level?: string; plain?: boolean }) {
  const k = (String(level ?? "unknown").toLowerCase() in LEVELS ? String(level).toLowerCase() : "unknown") as GiLevel;
  const L = LEVELS[k];
  return (
    <span className={`pd-level ${k}`}>
      <span className="ic" aria-hidden>
        {L.icon}
      </span>
      {L.label}
      {plain && <span className="plain">· {L.plain}</span>}
    </span>
  );
}

function TriggerChip({ t }: { t: GiTrigger | string }) {
  const label = typeof t === "string" ? t : t.label || t.code || "Trigger";
  const tip = typeof t === "string" ? "" : t.explanation || "";
  const chip = (
    <span className="pd-level unknown" style={{ fontWeight: 600, background: "#fff", cursor: tip ? "help" : undefined }} tabIndex={tip ? 0 : undefined}>
      {label.replace(/_/g, " ")}
      {tip ? " ⓘ" : ""}
    </span>
  );
  return tip ? (
    <Tooltip label={tip} multiline w={280} withArrow>
      {chip}
    </Tooltip>
  ) : (
    chip
  );
}

const isEscalation = (r: GiCoachResponse) => ["urgent", "emergency"].includes(r.safety?.urgency ?? "");
const suppressCoaching = (r: GiCoachResponse) => isEscalation(r) || Boolean(r.safety?.suppress_food_coaching);
const flagText = (f: unknown) => (typeof f === "string" ? f : f && typeof f === "object" ? String((f as Record<string, unknown>).label ?? (f as Record<string, unknown>).description ?? (f as Record<string, unknown>).code ?? JSON.stringify(f)) : String(f));

function SafetyBanner({ r }: { r: GiCoachResponse }) {
  const s = r.safety;
  if (!s) return null;
  const kind = s.urgency === "emergency" ? "emergency" : s.urgency === "urgent" ? "urgent" : s.urgency === "routine_review" ? "info" : null;
  if (!kind) return null;
  const title = kind === "emergency" ? "Get emergency help now" : kind === "urgent" ? "Contact your care team today" : "Worth mentioning at your next check-in";
  const flags = (s.red_flags ?? []).map(flagText);
  return (
    <div className={`pd-banner ${kind}`} role={kind === "info" ? undefined : "alert"}>
      <span className="bic" aria-hidden>
        {kind === "info" ? "i" : "!"}
      </span>
      <div>
        <Text fw={700}>{title}</Text>
        {s.message && <Text fz="sm">{s.message}</Text>}
        {flags.length > 0 && (
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {flags.map((f) => (
              <li key={f}>
                <Text fz="sm">{f}</Text>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Feedback({ executionId }: { executionId: string }) {
  const [sent, setSent] = useState<"up" | "down" | null>(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState("");
  const send = (label: "thumbs_up" | "thumbs_down", n?: string) => {
    void easyai.sendFeedback([{ source_type: "chain_execution", source_id: executionId, label, note: n || undefined, metadata: { client_event_id: `fb-${executionId}`, source: "passio-demo" } }]).catch(() => {});
    setSent(label === "thumbs_up" ? "up" : "down");
    setNoteOpen(false);
  };
  if (sent) {
    return (
      <Text fz="sm" c="dimmed">
        {sent === "up" ? "Thanks for the feedback." : "Thanks, that helps us improve."}
      </Text>
    );
  }
  return (
    <Stack gap="xs">
      <Group gap="xs">
        <Text fz="sm" c="dimmed">
          Was this helpful?
        </Text>
        <Button size="xs" variant="default" leftSection={<IconThumbUp size={14} />} onClick={() => send("thumbs_up")}>
          Yes
        </Button>
        <Button size="xs" variant="default" leftSection={<IconThumbDown size={14} />} onClick={() => setNoteOpen(true)}>
          Not really
        </Button>
      </Group>
      <Collapse in={noteOpen}>
        <Group gap="xs" align="flex-end">
          <Textarea style={{ flex: 1 }} placeholder="What was off? (optional)" value={note} onChange={(e) => setNote(e.currentTarget.value)} autosize minRows={1} />
          <Button size="xs" onClick={() => send("thumbs_down", note.trim().slice(0, 1000))}>
            Send
          </Button>
        </Group>
      </Collapse>
    </Stack>
  );
}

function Continue({ r, quick, onFollowUp, onReset }: { r: GiCoachResponse; quick?: boolean; onFollowUp: (q: string) => void; onReset: () => void }) {
  const fq = suppressCoaching(r) ? [] : (r.follow_up_questions ?? []);
  const list = quick ? fq.slice(0, 1) : fq;
  return (
    <div className="pd-continue">
      <Text fw={700}>{list.length ? "Questions for you" : "Keep the conversation going"}</Text>
      <Text fz="sm" c="dimmed" mb="sm">
        {list.length ? "Tap a question to answer it, or ask something new. Your earlier answer comes along as context." : isEscalation(r) ? "Please follow the advice above first." : "Ask something else and your earlier answer comes along as context."}
      </Text>
      {list.length > 0 && (
        <Stack gap={8}>
          {list.map((q) => (
            <button key={q} type="button" className="pd-fq" onClick={() => onFollowUp(q)}>
              {q}
            </button>
          ))}
        </Stack>
      )}
      <Group mt="md" gap="sm">
        {!isEscalation(r) && (
          <Button leftSection={<IconMessageCircle size={16} />} onClick={() => onFollowUp("")} style={{ flex: "1 1 200px" }}>
            Ask a follow-up
          </Button>
        )}
        <Button variant="default" leftSection={<IconRefresh size={16} />} onClick={onReset}>
          Start new
        </Button>
      </Group>
    </div>
  );
}

function Answer({ r, executionId, onFollowUp, onReset }: { r: GiCoachResponse; executionId: string; onFollowUp: (q: string) => void; onReset: () => void }) {
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const suppress = suppressCoaching(r);
  const fa = r.food_assessment ?? {};
  const meals = fa.meals ?? [];
  const recs = r.recommendations ?? [];
  const sources = r.sources ?? [];
  return (
    <Stack gap="lg">
      <SafetyBanner r={r} />
      {r.response_kind === "insufficient_information" && <div className="pd-banner info">💬 I need a bit more to go on</div>}
      {r.response_kind === "scope_boundary" && <div className="pd-banner info">🧭 That&apos;s outside what I can help with</div>}
      <div>
        {r.short_answer && (
          <Text fw={700} fz="lg" lh={1.4}>
            {r.short_answer}
          </Text>
        )}
        {r.long_answer && <Text mt="xs">{r.long_answer}</Text>}
      </div>
      {!suppress && (meals.length > 0 || fa.overall_level) && (
        <div>
          <Title order={4} mb="xs">
            Your meal
          </Title>
          {fa.overall_level && <LevelBadge level={fa.overall_level} />}
          {fa.overall_explanation && (
            <Text c="dimmed" mt={8}>
              {fa.overall_explanation}
            </Text>
          )}
          <Stack gap="sm" mt="sm">
            {meals.map((m, i) => (
              <div key={m.meal_id ?? i} className="pd-gi-meal">
                <Group justify="space-between" align="flex-start" gap="xs">
                  <Text fw={700}>{(m.food_identified ?? []).join(", ") || `Meal ${i + 1}`}</Text>
                  <LevelBadge level={m.level} plain={false} />
                </Group>
                {m.one_line_explanation && <Text mt={8}>{m.one_line_explanation}</Text>}
                {(m.triggers ?? []).length > 0 && (
                  <Group gap={6} mt="sm">
                    {(m.triggers ?? []).map((t, j) => (
                      <TriggerChip key={j} t={t} />
                    ))}
                  </Group>
                )}
                {(m.uncertainties ?? []).length > 0 && (
                  <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                    {(m.uncertainties ?? []).map((u) => (
                      <li key={u}>
                        <Text fz="sm" c="dimmed">
                          {u}
                        </Text>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </Stack>
        </div>
      )}
      {!suppress && recs.length > 0 && (
        <div>
          <Title order={4} mb="xs">
            What might help
          </Title>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            {recs.map((x, i) => (
              <div key={i} className="pd-rec-card">
                <Text fw={700}>{typeof x === "string" ? x : x.title || x.detail}</Text>
                {typeof x === "object" && x.detail && x.title && (
                  <Text fz="sm" c="dimmed">
                    {x.detail}
                  </Text>
                )}
              </div>
            ))}
          </SimpleGrid>
        </div>
      )}
      {!isEscalation(r) && (r.key_points ?? []).length > 0 && (
        <div>
          <Title order={4} mb="xs">
            Key points
          </Title>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {(r.key_points ?? []).map((k) => (
              <li key={k}>
                <Text>{k}</Text>
              </li>
            ))}
          </ul>
        </div>
      )}
      <Continue r={r} onFollowUp={onFollowUp} onReset={onReset} />
      {sources.length > 0 && (
        <div className="pd-rule" style={{ paddingTop: 12 }}>
          <Anchor component="button" type="button" fw={650} c="ink.8" onClick={() => setSourcesOpen((o) => !o)}>
            Sources ({sources.length}) {sourcesOpen ? "▾" : "▸"}
          </Anchor>
          <Collapse in={sourcesOpen}>
            <Stack gap={6} mt="xs">
              {sources.map((x, i) =>
                typeof x === "string" ? (
                  <Text key={i} fz="sm">
                    {x}
                  </Text>
                ) : (
                  <Text key={i} fz="sm">
                    {x.url ? (
                      <Anchor href={x.url} target="_blank" rel="noopener">
                        {x.title || x.url}
                      </Anchor>
                    ) : (
                      x.title || "Source"
                    )}
                    {(x.pdf_pages ?? []).length > 0 && <Text span c="dimmed">{` · pages ${(x.pdf_pages ?? []).join(", ")}`}</Text>}
                  </Text>
                ),
              )}
            </Stack>
          </Collapse>
        </div>
      )}
      <Box className="pd-rule" pt="sm">
        {(r.limitations ?? []).map((l) => (
          <Text key={l} fz="xs" c="dimmed">
            • {l}
          </Text>
        ))}
        {r.disclaimer && (
          <Text fz="xs" c="dimmed" mt={6}>
            {r.disclaimer}
          </Text>
        )}
        <Box mt="sm">{executionId && <Feedback executionId={executionId} />}</Box>
      </Box>
    </Stack>
  );
}

function QuickView({ r, onFollowUp, onReset }: { r: GiCoachResponse; onFollowUp: (q: string) => void; onReset: () => void }) {
  const suppress = suppressCoaching(r);
  const fa = r.food_assessment ?? {};
  const recs = (r.recommendations ?? []).slice(0, 3);
  return (
    <Stack gap="md">
      <SafetyBanner r={r} />
      {r.short_answer && (
        <Text fw={700} fz="lg">
          {r.short_answer}
        </Text>
      )}
      {!suppress && fa.overall_level && (
        <div>
          <LevelBadge level={fa.overall_level} />
          {fa.overall_explanation && (
            <Text c="dimmed" mt={6} lineClamp={2}>
              {fa.overall_explanation}
            </Text>
          )}
        </div>
      )}
      {!suppress && recs.length > 0 && (
        <div>
          <Text fw={700}>Try this</Text>
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            {recs.map((x, i) => (
              <li key={i}>{typeof x === "string" ? x : x.title || x.detail}</li>
            ))}
          </ol>
        </div>
      )}
      <Continue r={r} quick onFollowUp={onFollowUp} onReset={onReset} />
      {r.disclaimer && (
        <Text fz="xs" c="dimmed">
          {r.disclaimer}
        </Text>
      )}
    </Stack>
  );
}

interface Photo {
  dataUri: string;
}

/** Up to five photos: pick, take, drop or paste. Downscaled in the browser. */
function PhotoArea({ photos, onChange, disabled }: { photos: Photo[]; onChange: (p: Photo[]) => void; disabled?: boolean }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const camRef = useRef<HTMLInputElement>(null);
  const [active, setActive] = useState(false);
  const add = async (files: FileList | File[] | null | undefined) => {
    const list = [...(files ?? [])].filter((f) => f.type.startsWith("image/"));
    const next = [...photos];
    for (const f of list) {
      if (next.length >= MAX_IMAGES) break;
      next.push({ dataUri: await imageToDataUri(f) });
    }
    onChange(next);
  };
  return (
    <Stack gap="xs">
      <Box
        className="pd-dropzone"
        data-active={active}
        p="md"
        onDragOver={(e) => {
          e.preventDefault();
          setActive(true);
        }}
        onDragLeave={() => setActive(false)}
        onDrop={(e) => {
          e.preventDefault();
          setActive(false);
          void add(e.dataTransfer.files);
        }}
      >
        <Group justify="center" gap="sm">
          <IconPhotoUp size={22} color="var(--pd-ink)" />
          <Text fz="sm" fw={600}>
            Add a meal photo
          </Text>
          <Button size="xs" variant="default" onClick={() => fileRef.current?.click()} disabled={disabled || photos.length >= MAX_IMAGES}>
            Choose
          </Button>
          <Button size="xs" variant="filled" color="ink.8" leftSection={<IconCamera size={14} />} onClick={() => camRef.current?.click()} disabled={disabled || photos.length >= MAX_IMAGES}>
            Take photo
          </Button>
        </Group>
        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => void add(e.currentTarget.files)} />
        <input ref={camRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => void add(e.currentTarget.files)} />
      </Box>
      {photos.length > 0 && (
        <Group gap="sm">
          {photos.map((p, i) => (
            <Box key={i} pos="relative">
              <Image src={p.dataUri} alt={`Attached photo ${i + 1}`} w={84} h={84} radius="md" fit="cover" />
              <ActionIcon pos="absolute" top={-8} right={-8} radius="xl" size="sm" color="ink.8" onClick={() => onChange(photos.filter((_, j) => j !== i))} aria-label={`Remove photo ${i + 1}`}>
                <IconX size={12} />
              </ActionIcon>
            </Box>
          ))}
        </Group>
      )}
    </Stack>
  );
}

const MARK_ASK = "Enter your follow-up question here:";
const MARK_ANSWER = "Your answer:";

interface LastRun {
  resp: GiCoachResponse;
  executionId: string;
  input: Record<string, unknown>;
  photos: Photo[];
}

/** Port of the GI coach demo app (North America profile, English) on the glp1_assistant chain. */
export function GiCoachPage() {
  const [tab, setTab] = useState<string>("ask");
  const [ask, setAsk] = useState("");
  const [d, setD] = useState<Details>(EMPTY_DETAILS);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [moreOpen, setMoreOpen] = useState(false);
  const [advOpen, setAdvOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [respTab, setRespTab] = useState<string>("answer");
  const [last, setLast] = useState<LastRun | null>(null);
  const [reattach, setReattach] = useState(false);
  const [uploadNote, setUploadNote] = useState<string | null>(null);
  const askRef = useRef<HTMLTextAreaElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const r = useChainRun();
  const setField = <K extends keyof Details>(k: K, v: Details[K]) => setD((x) => ({ ...x, [k]: v }));

  const ready = tab === "ask" ? Boolean(ask.trim() || photos.length) : Boolean(d.question.trim() || d.food_text.trim() || d.barcodes.trim() || photos.length);
  const hint = r.running ? "The coach is working on your answer…" : ready ? "Ready · North America, answers in English." : tab === "ask" ? "Add a message or a photo to get started." : "Fill in a question, a food, a barcode or add a photo.";

  useEffect(() => {
    if (last) setRespTab("answer");
  }, [last]);

  const submit = async () => {
    if (!ready || r.running) return;
    const input: Record<string, unknown> = {};
    if (tab === "ask") input.question = ask.trim();
    else {
      for (const [k, v] of Object.entries(d)) {
        if (v === "" || v === undefined) continue;
        if (k === "foods") {
          try {
            input.foods = JSON.parse(String(v));
          } catch {
            input.foods = v;
          }
        } else input[k] = typeof v === "string" ? v.trim() : v;
      }
    }
    input.language_mode = "english";
    input.output_language = "en";
    input.request_id = crypto.randomUUID();
    const sentPhotos = photos.slice();
    setUploadNote(null);
    if (sentPhotos.length) {
      const urls = await Promise.all(
        sentPhotos.map(async (p) => {
          try {
            const blob = await (await fetch(p.dataUri)).blob();
            return (await easyai.uploadTemp(blob, "photo.jpg")).public_url;
          } catch {
            return null;
          }
        }),
      );
      const ok = urls.filter((u): u is string => Boolean(u));
      if (ok.length < sentPhotos.length) setUploadNote(`${sentPhotos.length - ok.length} photo(s) couldn't be uploaded and were left out.`);
      if (ok.length) input.attached_image_urls = ok;
    }
    const exe = await r.run(CHAIN_KEY, input);
    if (!exe) return;
    const resp = giCoachFromOutput(sinkOutput(exe, "node-3"));
    if (resp) setLast({ resp, executionId: exe.id, input, photos: sentPhotos });
  };

  const followUp = (question: string) => {
    if (!last) return;
    const resp = last.resp;
    const fa = resp.food_assessment ?? {};
    const prev = String(last.input.question || last.input.food_text || "");
    const iA = Math.max(prev.lastIndexOf(MARK_ASK) >= 0 ? prev.lastIndexOf(MARK_ASK) + MARK_ASK.length : -1, prev.lastIndexOf(MARK_ANSWER) >= 0 ? prev.lastIndexOf(MARK_ANSWER) + MARK_ANSWER.length : -1);
    let orig = (iA > 0 ? prev.slice(iA) : prev).trim().replace(/\s+/g, " ");
    if (orig.length > 280) orig = `${orig.slice(0, 277)}…`;
    let symptoms = String(last.input.symptoms || "");
    if (!symptoms) symptoms = prev.match(/^Symptoms: (.+)$/m)?.[1] ?? "";
    const foods = (fa.meals ?? []).flatMap((m) => m.food_identified ?? []);
    const coachQs = question ? [question] : suppressCoaching(resp) ? [] : (resp.follow_up_questions ?? []);
    const lines: string[] = [];
    if (orig) lines.push(`Earlier I asked: ${orig}`);
    if (foods.length) lines.push(`Foods: ${foods.join(", ")}${fa.overall_level ? ` (overall: ${fa.overall_level})` : ""}`);
    if (symptoms) lines.push(`Symptoms: ${symptoms}`);
    if (resp.short_answer) lines.push(`Coach said: ${resp.short_answer}`);
    if (coachQs.length === 1) lines.push(`Coach asked: ${coachQs[0]}`);
    else if (coachQs.length > 1) lines.push(`Coach asked:\n${coachQs.map((q) => `- ${q}`).join("\n")}`);
    const text = `${lines.join("\n")}\n\n\n${question ? MARK_ANSWER : MARK_ASK}\n`;
    if (question) void easyai.sendFeedback([{ source_type: "chain_execution", source_id: last.executionId, label: "click", item_ref: question.slice(0, 200), metadata: { source: "passio-demo" } }]).catch(() => {});
    setTab("ask");
    setAsk(text);
    setPhotos([]);
    setReattach(last.photos.length > 0);
    inputRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => {
      const ta = askRef.current;
      if (!ta) return;
      ta.focus({ preventScroll: true });
      ta.setSelectionRange(text.length, text.length);
      ta.scrollTop = ta.scrollHeight;
    }, 250);
  };

  const clearInput = () => {
    setAsk("");
    setD(EMPTY_DETAILS);
    setPhotos([]);
    setReattach(false);
    setUploadNote(null);
  };
  const reset = () => {
    r.cancel();
    r.reset();
    clearInput();
    setLast(null);
    setTab("ask");
    window.scrollTo({ top: 0, behavior: "smooth" });
    setTimeout(() => askRef.current?.focus({ preventScroll: true }), 250);
  };

  return (
    <RequireFeature feature="gi_coach">
      <PageHeader
        eyebrow="EasyAI chain · glp1_assistant"
        title="How might this meal sit with you?"
        description="Ask about food, symptoms or everyday life on GLP-1 treatment. Describe a meal, share a photo or scan a barcode. Educational guidance only, not medical advice."
      />
      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg" style={{ alignItems: "start" }}>
        <Card ref={inputRef}>
          <Tabs value={tab} onChange={(v) => setTab(v ?? "ask")} variant="pills" radius="md">
            <Tabs.List grow mb="md">
              <Tabs.Tab value="ask">Ask</Tabs.Tab>
              <Tabs.Tab value="details">Details</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="ask">
              <Stack gap="sm">
                <Textarea
                  ref={askRef}
                  label="Your message"
                  placeholder="Tell me what you ate, how you're feeling, or ask a question. For example: I'm 3 weeks in, feeling a bit queasy, and had a burger and fries for lunch. Is that why?"
                  autosize
                  minRows={6}
                  maxRows={14}
                  value={ask}
                  onChange={(e) => setAsk(e.currentTarget.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      void submit();
                    }
                  }}
                  disabled={r.running}
                />
                {!ask && !last && (
                  <Group gap={6}>
                    {STARTERS.map((s) => (
                      <Button key={s} size="compact-xs" variant="outline" color="gray" fw={500} onClick={() => setAsk(s)} styles={{ label: { whiteSpace: "normal", textAlign: "left" } }} h="auto" py={4}>
                        {s}
                      </Button>
                    ))}
                  </Group>
                )}
                {reattach && last && (
                  <Group gap="xs" className="pd-banner info" p="xs">
                    <Text fz="sm">Previous photo not re-sent.</Text>
                    <Anchor
                      component="button"
                      type="button"
                      fz="sm"
                      fw={650}
                      onClick={() => {
                        setPhotos(last.photos);
                        setReattach(false);
                      }}
                    >
                      Re-attach
                    </Anchor>
                  </Group>
                )}
              </Stack>
            </Tabs.Panel>
            <Tabs.Panel value="details">
              <Stack gap="sm">
                <Textarea label="Your question" description="Anything you'd like to understand." placeholder="e.g. Why do fatty meals feel heavy on a GLP-1?" autosize minRows={2} value={d.question} onChange={(e) => setField("question", e.currentTarget.value)} />
                <Textarea label="Food, meal or food log" description="One meal, or a whole day." placeholder="e.g. Cheeseburger and large fries. Or a day log: Breakfast: … Lunch: … Dinner: …" autosize minRows={2} value={d.food_text} onChange={(e) => setField("food_text", e.currentTarget.value)} />
                <Textarea label="Current symptoms" description="Leave blank if you'd rather not say." placeholder="e.g. mild nausea since lunch, keeping fluids down. Type none if there are none." autosize minRows={2} value={d.symptoms} onChange={(e) => setField("symptoms", e.currentTarget.value)} />
                <Select
                  label="Treatment stage"
                  data={[
                    { value: "unknown", label: "Unknown" },
                    { value: "starting", label: "Just starting" },
                    { value: "recent_increase", label: "Recent prescribed dose increase" },
                    { value: "maintenance", label: "Maintenance" },
                  ]}
                  value={d.treatment_stage}
                  onChange={(v) => setField("treatment_stage", v ?? "unknown")}
                  allowDeselect={false}
                />
                <Anchor component="button" type="button" fw={650} c="ink.8" ta="left" onClick={() => setMoreOpen((o) => !o)}>
                  {moreOpen ? "–" : "+"} More details
                </Anchor>
                <Collapse in={moreOpen}>
                  <Stack gap="sm">
                    <Group gap="xs" align="flex-end" wrap="nowrap">
                      <TextInput style={{ flex: 1 }} label="Barcode / UPC" description="From a packaged food or drink." placeholder="e.g. 012345678905 (comma separate several)" value={d.barcodes} onChange={(e) => setField("barcodes", e.currentTarget.value)} />
                      <Tooltip label="Scan with your camera">
                        <ActionIcon size={36} variant="default" onClick={() => setScanning(true)} aria-label="Scan barcode">
                          <IconBarcode size={18} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                    <TextInput label="Medicine name" description="Used as context only." placeholder="Optional" value={d.medication_name} onChange={(e) => setField("medication_name", e.currentTarget.value)} />
                    <NumberInput label="Weeks on treatment" placeholder="Optional" min={0} allowDecimal={false} value={d.weeks_on_treatment} onChange={(v) => setField("weeks_on_treatment", v === "" ? "" : Number(v))} />
                    <Select
                      label="Fluid restriction from your care team"
                      data={[
                        { value: "unknown", label: "Unknown" },
                        { value: "yes", label: "Yes" },
                        { value: "no", label: "No" },
                      ]}
                      value={d.fluid_restriction}
                      onChange={(v) => setField("fluid_restriction", v ?? "unknown")}
                      allowDeselect={false}
                    />
                    <TextInput label="Dietary constraints" placeholder="e.g. vegetarian, no dairy" value={d.dietary_constraints} onChange={(e) => setField("dietary_constraints", e.currentTarget.value)} />
                    <Textarea label="Additional context" placeholder="Optional" autosize minRows={2} value={d.additional_context} onChange={(e) => setField("additional_context", e.currentTarget.value)} />
                  </Stack>
                </Collapse>
                <Anchor component="button" type="button" fw={650} c="ink.8" ta="left" onClick={() => setAdvOpen((o) => !o)}>
                  {advOpen ? "–" : "+"} Advanced
                </Anchor>
                <Collapse in={advOpen}>
                  <Textarea
                    label="Recognized foods JSON"
                    description="For integrations that already ran food recognition."
                    placeholder='[{"id":"food-1","name":"Cheeseburger","meal_id":"meal-1","portion_description":"1 burger"}]'
                    autosize
                    minRows={2}
                    styles={{ input: { fontFamily: "var(--mantine-font-family-monospace)" } }}
                    value={d.foods}
                    onChange={(e) => setField("foods", e.currentTarget.value)}
                  />
                </Collapse>
              </Stack>
            </Tabs.Panel>
          </Tabs>
          <Box mt="md">
            <PhotoArea photos={photos} onChange={setPhotos} disabled={r.running} />
          </Box>
          <Group justify="space-between" mt="md" pt="md" className="pd-rule" wrap="wrap" gap="sm">
            <Text fz="sm" c="dimmed" style={{ flex: 1, minWidth: 180 }}>
              {hint}
            </Text>
            <Group gap="xs">
              <Button variant="default" onClick={clearInput} disabled={r.running}>
                Clear
              </Button>
              <Button size="md" onClick={() => void submit()} disabled={!ready} loading={r.running}>
                Ask the coach
              </Button>
            </Group>
          </Group>
          {uploadNote && (
            <Text fz="xs" c="orange.8" mt="xs">
              {uploadNote}
            </Text>
          )}
        </Card>

        <Stack gap="md">
          {r.running && <RunProgressCard title="Working on your answer" progress={r.progress} stages={STAGES} onCancel={r.cancel} />}
          <ChainError error={r.error} onRetry={() => void submit()} />
          {!last && !r.running && !r.error && (
            <Card ta="center" py={40}>
              <Stack align="center" gap="xs">
                <IconStethoscope size={44} color="var(--pd-green)" />
                <Title order={3} fz={20}>
                  Your answer will appear here
                </Title>
                <Text c="dimmed" maw={380}>
                  Describe a meal, share a photo, or ask how something might feel on your treatment.
                </Text>
              </Stack>
            </Card>
          )}
          {last && (
            <Card style={{ opacity: r.running ? 0.45 : 1, pointerEvents: r.running ? "none" : undefined }}>
              <Tabs value={respTab} onChange={(v) => setRespTab(v ?? "answer")} variant="pills" radius="md">
                <Tabs.List grow mb="md">
                  <Tabs.Tab value="answer">Answer</Tabs.Tab>
                  <Tabs.Tab value="quick">Quick view</Tabs.Tab>
                  <Tabs.Tab value="json">JSON</Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value="answer">
                  <Answer r={last.resp} executionId={last.executionId} onFollowUp={followUp} onReset={reset} />
                </Tabs.Panel>
                <Tabs.Panel value="quick">
                  <QuickView r={last.resp} onFollowUp={followUp} onReset={reset} />
                </Tabs.Panel>
                <Tabs.Panel value="json">
                  <Stack gap="sm">
                    <Text fz="sm" fw={650}>
                      Request sent
                    </Text>
                    <JsonView value={{ chain: "glp1_assistant", input_data: last.input }} />
                    <Group justify="space-between">
                      <Text fz="sm" fw={650}>
                        Response
                      </Text>
                      <CopyButton value={JSON.stringify(last.resp, null, 2)}>
                        {({ copied, copy }) => (
                          <Button size="xs" variant="default" onClick={copy}>
                            {copied ? "Copied" : "Copy response"}
                          </Button>
                        )}
                      </CopyButton>
                    </Group>
                    <JsonView value={last.resp} />
                  </Stack>
                </Tabs.Panel>
              </Tabs>
            </Card>
          )}
        </Stack>
      </SimpleGrid>
      <Text fz="xs" c="dimmed" ta="center" mt="xl">
        <b>Evaluation build, not medical advice.</b> Talk to your prescriber or pharmacist about your treatment.
      </Text>
      <BarcodeScanner
        opened={scanning}
        onClose={() => setScanning(false)}
        onDetected={(code) => {
          setField("barcodes", d.barcodes.trim() ? `${d.barcodes.trim()}, ${code}` : code);
          setScanning(false);
        }}
      />
    </RequireFeature>
  );
}
